/**
 * NoChargeLine.tsx — "No charge until your treatment is approved.", highlighted,
 * in ONE look wherever money timing matters.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey copy pass
 * (2026-09-26).
 *
 * WHY A COMPONENT. The CTO called this line important and asked for it to stay
 * prominent ("No charge until your treatment is approved." — CLAUDE.md "PATIENT
 * COPY — HOW WE WRITE"). It was a tinted, icon-led box on "What happens next"
 * and a plain grey sentence under the plan screen's button, worded differently
 * ("Your card won't be charged until a clinician approves your treatment.").
 * The plan screen now says it once, in the SAME words (`NO_CHARGE_UNTIL_APPROVED`
 * from `serviceClaims.ts`) and the same highlighted box, so the two screens that
 * make the claim make it identically.
 *
 * The shield is decorative (`aria-hidden`); the sentence is the claim. `compact`
 * is the same box one step smaller, for the plan rail's pinned foot, where every
 * pixel is budgeted (PlanSelection's rail note). `icon={false}` drops the shield
 * and keeps the box: in the 20rem rail (880–1199px) the shield and its gap are
 * what wrapped the sentence to a second line — measured in Chrome, the words are
 * 282.9px at 13.5px against the foot's 310px, and 332.9px with the shield — and
 * that line came out of the agreement's reading window (review, 2026-09-26).
 *
 * ⚠️ R28 / pre-eligibility safe: it names no medicine, no price and no form, so
 * it may sit on "What happens next" (public) and on the plan screen alike.
 */
import type { CSSProperties } from 'react';
import { IconSafety } from '../ui/icons';
import { NO_CHARGE_UNTIL_APPROVED } from './serviceClaims';

const boxStyle: CSSProperties = {
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
  margin: '4px 0 0',
  padding: '10px 14px',
  borderRadius: 'var(--gps-r-md)',
  background: 'var(--gps-primary-light)',
  color: 'var(--gps-green-text)',
  fontSize: '15px',
  fontWeight: 600,
  lineHeight: 1.4,
};

const compactStyle: CSSProperties = {
  gap: '8px',
  padding: '8px 12px',
  fontSize: '13.5px',
};

export function NoChargeLine({
  compact = false,
  icon = true,
  className,
  style,
}: {
  compact?: boolean;
  /** False ⇒ the same highlighted box without the decorative shield (see above). */
  icon?: boolean;
  /** A paint/placement hook only (e.g. "What happens next"'s `gps-whn-no-charge`). */
  className?: string;
  /** Placement only (a margin) — never the colours, which are the claim's highlight. */
  style?: CSSProperties;
}) {
  return (
    <p className={className} style={{ ...boxStyle, ...(compact ? compactStyle : null), ...style }}>
      {icon ? (
        <span aria-hidden="true" style={{ display: 'grid', flex: 'none' }}>
          <IconSafety size={compact ? 18 : 20} />
        </span>
      ) : null}
      <span>{NO_CHARGE_UNTIL_APPROVED}</span>
    </p>
  );
}
